import { useRef, useEffect, useCallback } from 'react';

// ----------------------------------------------------------------------

export function useMessagesScroll(messages, loading = false, conversationId = null) {
  const scrollableRef = useRef(null);
  const messagesEndRef = useRef(null);
  const prevConversationIdRef = useRef(conversationId);
  const prevCountRef = useRef(0);

  const flatMessages = Object.values(messages || {}).flat();
  const currentCount = flatMessages.length;
  const lastMessage = flatMessages[flatMessages.length - 1];
  const lastMessageLength = lastMessage?.body?.length || 0;

  const scrollToBottom = useCallback((smooth = false) => {
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'end'
        });
      } catch (e) {
        // Fallback below
      }
    }

    if (scrollableRef.current) {
      const el = scrollableRef.current;
      if (smooth && typeof el.scrollTo === 'function') {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    const prevConversationId = prevConversationIdRef.current;
    const prevCount = prevCountRef.current;

    prevConversationIdRef.current = conversationId;
    prevCountRef.current = currentCount;

    // 1. Conversation switched -> scroll to bottom
    if (String(prevConversationId) !== String(conversationId)) {
      const timer = setTimeout(() => scrollToBottom(false), 50);
      return () => clearTimeout(timer);
    }

    // 2. New message added, loading state changed, or streaming token arrived:
    if (currentCount > prevCount || loading || lastMessage?.isStreaming) {
      const timer = setTimeout(() => scrollToBottom(false), 20);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [conversationId, currentCount, loading, lastMessageLength, lastMessage?.isStreaming, scrollToBottom]);

  // Initial mount scroll
  useEffect(() => {
    const timer = setTimeout(() => scrollToBottom(false), 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { scrollableRef, messagesEndRef, scrollToBottom };
}
